/**
 * lineSteps — each Sales Order line's process steps, with how many of THIS
 * order's pieces have finished each one (Mike, 2026-10-20, SO-1139: "each part
 * shows all of the process steps required to produce the part with a sum of how
 * many have completed each step").
 *
 * The Production Detail strip answers that per job; a line's parts are spread
 * over several jobs (SO-1139 FAB311162 = its own job #10138 plus 93 of SO-1131's
 * job #10136), so the line needs the sum. Per part:
 *   - every job's chain comes from groupJobTasks (same folding as the Production
 *     Detail: consecutive same-station tasks = one step, Cancelled / Voided tasks
 *     never count). Shipping is left out: the table has its own Shipped column
 *     (Mike, 2026-10-20);
 *   - a shared laser sheet (or saw list) reports sheets, not pieces: pieces done =
 *     the part's pieces on it × sheets run ÷ sheets required, less the laser
 *     defects recorded against the part (qty_completed elsewhere is also
 *     quantity − that task's defects);
 *   - a job ALLOCATED from another order counts only this order's share
 *     (incoming_allocations by source_job_id: 93 of #10136's 171), scaled down;
 *     pieces this order's own jobs allocate OUT are netted from its own jobs;
 *   - steps from several jobs merge by station in routing order;
 *   - a part on several lines (as /truth splits its Shipped across them):
 *     each step's count is spread over the lines, each line covered up to its
 *     ordered quantity, leftover on the last line.
 * A line whose part has no job yet takes its steps from the part's own routing
 * (`process_steps`), all at 0.
 *
 * Chip state for a line: green ("done") once the step's count reaches the
 * line's ordered quantity, yellow ("partial") when some pieces are through or a
 * task there is running, gray ("zero") when nothing has reached it.
 *
 * Outside steps (Jesse, 2026-10-20: "clearly indicate that these parts are at the
 * coater, not just sitting waiting to be sent"): jobq-api puts each task's live
 * outsourcing manifest lines on it (`outside_processing[]`, TASK units). Sending a
 * manifest leaves the task Pending, so the chip reads the lines: blue "out" (a
 * truck) while pieces are at the vendor, red-bordered "out_late" once its due-back
 * date has passed, gray dashed "staged" while they are only on a Draft manifest.
 * Pieces back count as done (a part return never touches qty_completed until the
 * whole line is back). A manifest is ONE order's paper: its pieces count for that
 * order, never by share (KC-SVC-2026-0025 carried SO-1131's 78 of job #10136, none
 * of SO-1139's 93); a paper with no order counts by the job's share. Exception: when
 * the plan had nowhere else to put them, the job's HOME order's paper carries the
 * pieces allocated to us too, so a home paper carrying more than the home order's
 * share hands the excess to this order. Per task, never more than this order's
 * pieces of it (back first, then out, then staged); rejected pieces are neither out
 * nor back (in the building, waiting for a rework line).
 * Pure: no React, no fetch.
 */
import { groupJobTasks } from "../../utils/groupJobTasks";

const TERMINAL = new Set(["Cancelled", "Voided"]);
const RUNNING = new Set(["In Progress", "Paused", "Partial"]);

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
// Pieces are whole; a share like 117 × 93/171 rounds down (never claims a piece
// the order doesn't have). The epsilon keeps 171 × 93/171 at exactly 93.
const floorPieces = (v) => Math.max(0, Math.floor(v + 1e-9));

/** "2026-10-23" → "10/23". */
export function monthDay(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  return m ? `${Number(m[2])}/${Number(m[3])}` : "";
}

const truncate = (text, n) => (text && text.length > n ? `${text.slice(0, n - 1)}…` : text || "");

const sumOf = (arr, f) => arr.reduce((a, x) => a + f(x), 0);
const linesOf = (task) => (task && Array.isArray(task.outside_processing) ? task.outside_processing : []);

/** Raw (weighted, unrounded) totals of manifest lines + the papers with weight > 0. */
function rawOutside(lines, weight) {
  const raw = { out: 0, back: 0, staged: 0, scrapped: 0, papers: [] };
  (lines || []).forEach((ln) => {
    if (!ln) return;
    const w = weight(ln);
    if (!(w > 0)) return;
    const draft = ln.status === "Draft";
    const paper = {
      opo_number: ln.opo_number,
      vendor_name: ln.vendor_name || null,
      status: ln.status,
      sales_order_id: ln.sales_order_id ?? null,
      sales_order_number: ln.sales_order_number || null,
      sent_at: ln.sent_at || null,
      due_back: ln.due_back || null,
      out: draft ? 0 : num(ln.at_vendor) * w,
      back: draft ? 0 : num(ln.returned) * w,
      staged: draft ? num(ln.sent) * w : 0,
      scrapped: draft ? 0 : num(ln.scrapped) * w,
      rejected: draft ? 0 : num(ln.rejected) * w,
    };
    raw.out += paper.out;
    raw.back += paper.back;
    raw.staged += paper.staged;
    raw.scrapped += paper.scrapped;
    raw.papers.push(paper);
  });
  return raw;
}

/**
 * Whole pieces from raw totals, rounded ONCE on the running sums (back, back + out,
 * back + out + staged) so a fraction never loses a piece between two buckets.
 * `base` = pieces done some other way (completed tasks with no paper), counted as back.
 */
function finishOutside(raw, papers, today, base = 0) {
  const b = base + raw.back;
  const back = floorPieces(b);
  const out = floorPieces(b + raw.out) - back;
  const staged = floorPieces(b + raw.out + raw.staged) - floorPieces(b + raw.out);
  // Due date and vendor from the papers holding at least a whole piece out.
  const holding = papers.filter((p) => p.out >= 1 - 1e-9);
  const dueBack = holding.map((p) => p.due_back).filter(Boolean).sort()[0] || null;
  const vendors = [...new Set(holding.map((p) => p.vendor_name).filter(Boolean))];
  return {
    back, out, staged, scrapped: floorPieces(raw.scrapped), papers, dueBack, vendors,
    overdue: !!(out > 0 && dueBack && today && dueBack < today),
  };
}

/**
 * Totals of manifest lines (jobq-api `outside_processing[]`, TASK units).
 * `weight(line)` = the share of the line's pieces that are this view's (1 for a job's
 * own view). out = at the vendor, back = returned, staged = on a Draft.
 */
export function outsideSummary(lines, { weight = () => 1, today = null } = {}) {
  const raw = rawOutside(lines, weight);
  return finishOutside(raw, raw.papers, today);
}

/**
 * This order's part of ONE task's manifest lines.
 * ctx = { soId, share (this order's pieces / the task's), homeSoId (an allocated
 * job's own order), allocated }. Returns raw totals, or null when no paper of this
 * order is on the task. An allocated job's share is exact, so its task is capped at
 * this order's pieces of it; an own job's task only at the task's pieces (the
 * order-level cap in partStepTotals takes off what went to other orders: their
 * allocation rows name no source job, so a per-task share would be a guess).
 */
function attributeTask(task, ctx) {
  const lines = linesOf(task);
  const qty = num(task.quantity);
  const mine = qty * ctx.share;
  let homeWeight = 0;
  if (ctx.soId != null && ctx.homeSoId != null) {
    // The home order's paper took more than the home order's share: the rest is ours,
    // up to what our own papers don't already carry.
    const homeSent = sumOf(lines.filter((l) => l && l.sales_order_id === ctx.homeSoId), (l) => num(l.sent));
    const ownSent = sumOf(lines.filter((l) => l && l.sales_order_id === ctx.soId), (l) => num(l.sent));
    const excess = Math.max(0, homeSent - Math.max(0, qty - mine));
    const claim = Math.min(excess, Math.max(0, mine - ownSent));
    homeWeight = homeSent > 0 ? claim / homeSent : 0;
  }
  const weight = (ln) => {
    if (ctx.soId == null || ln.sales_order_id == null) return ctx.share;
    if (ln.sales_order_id === ctx.soId) return 1;
    if (ctx.homeSoId != null && ln.sales_order_id === ctx.homeSoId) return homeWeight;
    return 0;
  };
  const raw = rawOutside(lines, weight);
  if (raw.papers.length === 0) return null;
  if (qty > 0) {
    let room = ctx.allocated ? mine : qty;
    raw.back = Math.min(raw.back, room);
    room -= raw.back;
    raw.out = Math.min(raw.out, room);
    room -= raw.out;
    raw.staged = Math.min(raw.staged, Math.max(0, room));
  }
  return raw;
}

/** "Alexander MFG" / "2 vendors" for the papers with pieces out. */
export function vendorLabel(vendors, max = 16) {
  if (!vendors || vendors.length === 0) return "vendor";
  if (vendors.length > 1) return `${vendors.length} vendors`;
  return truncate(vendors[0], max);
}

/**
 * A job's own step at an outside vendor, for the job routing strips (the SO page's Production
 * Detail and the phone order screen's Production section). The job's own view: every paper on
 * the step's tasks counts, whichever order it is for. `step` = a groupJobTasks step; `today` =
 * the shop day (a paper past its due-back date turns the chip red); `shownSoId` = the order on
 * screen.
 *
 * null when no manifest line is on the step. Else { state, sub, paperNote }: while the step is
 * open, "out" / "out_late" ("200 back · 430 out · Alexander MFG · due 10/23") with pieces at the
 * vendor, ending "· for SO-1131" when every paper holding them is another order's, or "staged"
 * ("50 on a draft manifest") with pieces only on a Draft; state and sub are null otherwise (the
 * caller's own chip stands). paperNote = " · KC-SVC-2026-0025 (Sent, Alexander MFG)" for the title.
 */
export function outsideChip(step, { today = null, shownSoId = null } = {}) {
  const os = outsideSummary(((step && step.tasks) || []).flatMap(linesOf), { today });
  if (os.papers.length === 0) return null;
  let state = null;
  let sub = null;
  if (!step.terminal && step.status !== "Completed") {
    if (os.out > 0) {
      state = os.overdue ? "out_late" : "out";
      const holding = os.papers.filter((p) => p.out >= 1);
      const owners = [...new Set(holding.map((p) => p.sales_order_id))];
      const forOther = shownSoId != null && owners.length > 0 && owners.every((o) => o != null && o !== shownSoId)
        ? ` · for ${[...new Set(holding.map((p) => p.sales_order_number).filter(Boolean))].join(", ")}`
        : "";
      const due = os.dueBack ? ` · ${os.overdue ? "was due" : "due"} ${monthDay(os.dueBack)}` : "";
      sub = `${os.back > 0 ? `${os.back} back · ` : ""}${os.out} out · ${vendorLabel(os.vendors, 14)}${due}${forOther}`;
    } else if (os.staged > 0) {
      state = "staged";
      sub = `${os.staged} on a draft manifest`;
    }
  }
  const paperNote = " · " + os.papers.map((p) => `${p.opo_number || "manifest"} (${p.status}${p.vendor_name ? `, ${p.vendor_name}` : ""})`).join(", ");
  return { state, sub, paperNote };
}

const SHOP_MONTH_DAY = new Intl.DateTimeFormat("en-US", { timeZone: "America/Chicago", month: "numeric", day: "numeric" });

/** A timestamp's shop (Central) day as "10/19"; "" when unreadable. */
export function shopMonthDay(ts) {
  const d = ts ? new Date(ts) : null;
  return d && !Number.isNaN(d.getTime()) ? SHOP_MONTH_DAY.format(d) : "";
}

/** One tooltip line per paper: "KC-SVC-2026-0025 · Alexander MFG: 420 out, 0 back · sent 10/19 · due 10/23". */
function paperLines(papers, today) {
  return (papers || []).map((p) => {
    const counts = p.status === "Draft"
      ? `${floorPieces(p.staged)} on a draft, not sent`
      : `${floorPieces(p.out)} out, ${floorPieces(p.back)} back${p.scrapped >= 1 ? `, ${floorPieces(p.scrapped)} scrapped` : ""}${p.rejected >= 1 ? `, ${floorPieces(p.rejected)} rejected (in the shop for rework)` : ""}`;
    const when = [
      p.sent_at ? `sent ${shopMonthDay(p.sent_at)}` : null,
      p.due_back ? `${p.out > 0 && today && p.due_back < today ? "OVERDUE, was due" : "due"} ${monthDay(p.due_back)}` : null,
    ].filter(Boolean).join(" · ");
    return `${p.opo_number || "manifest"}${p.vendor_name ? ` · ${p.vendor_name}` : ""}: ${counts}${when ? ` · ${when}` : ""}`;
  });
}

/**
 * Pieces of `partId` a shared sheet / list task has turned out, less the part's
 * defects on that task. Null when the task doesn't carry the part.
 * Count and target mirror the Production Detail's shared row (sheets, else the
 * task's own quantity); a Completed task that reports no count counts as run.
 */
export function sharedTaskPieces(task, partId) {
  const p = ((task && task.parts) || []).find((x) => x && x.part_id === partId);
  if (!p) return null;
  const planned = num(p.quantity);
  const target = num(task.sheet_required) || num(task.quantity);
  const count = task.sheet_count != null ? num(task.sheet_count) : num(task.qty_completed);
  let frac;
  if (task.status === "Completed" && count === 0) frac = 1;
  else if (target > 0) frac = Math.min(1, count / target);
  else frac = task.status === "Completed" ? 1 : 0;
  const defects = ((task && task.defects_by_part) || [])
    .filter((d) => d && d.part_id === partId)
    .reduce((a, d) => a + num(d.defective_quantity), 0);
  const cut = floorPieces(planned * frac);
  return { planned, done: Math.max(0, cut - defects), defects };
}

/** One job's chain for one part (from a groupJobTasks group), before any share. */
function chainForGroup(group) {
  const chain = [];
  const push = (key, entry) => {
    const last = chain[chain.length - 1];
    if (last && last.key === key) {
      last.done += entry.done;
      last.planned += entry.planned;
      last.defects += entry.defects;
      last.running = last.running || entry.running;
      last.tasks += entry.tasks;
      last.completedTasks += entry.completedTasks;
      last.taskList = last.taskList.concat(entry.taskList);
      return;
    }
    chain.push({ key, ...entry });
  };
  group.sharedTasks.forEach((s) => {
    if (TERMINAL.has(s.status)) return;
    const pcs = sharedTaskPieces(s, group.part_id);
    if (!pcs) return;
    push(`${s.workstation || ""}|${s.sub_process || ""}`, {
      label: s.sub_process ? `${s.workstation} (${s.sub_process})` : s.workstation,
      workstation: s.workstation,
      done: pcs.done,
      planned: pcs.planned,
      defects: pcs.defects,
      running: RUNNING.has(s.status),
      tasks: 1,
      completedTasks: s.status === "Completed" ? 1 : 0,
      taskList: [],
    });
  });
  group.steps.forEach((step) => {
    if (step.terminal || step.workstation === "Shipping") return;
    const decoupled = step.defectRows.reduce((a, d) => a + num(d.defective_quantity), 0);
    push(step.key, {
      label: step.label,
      workstation: step.workstation,
      done: num(step.qty_completed),
      planned: num(step.quantity),
      defects: num(step.defect_quantity) + decoupled,
      running: RUNNING.has(step.status),
      tasks: step.tasks.length,
      completedTasks: step.setsDone,
      taskList: step.tasks,
    });
  });
  // Occurrence index: a part that visits one station twice keeps two steps.
  const seen = {};
  chain.forEach((c) => {
    seen[c.key] = (seen[c.key] || 0) + 1;
    c.id = `${c.key}#${seen[c.key]}`;
  });
  return chain;
}

/** The part's pieces on this job: its sheet pieces, else the biggest step. */
function jobPartQty(chain) {
  return chain.reduce((m, c) => Math.max(m, c.planned), 0);
}

const allocQty = (a) => num(a && (a.intent_quantity != null ? a.intent_quantity : a.quantity));

/** Every allocation on the part's lines once (a pool repeats them per line). */
function uniqueAllocations(lines, field) {
  const byId = new Map();
  lines.forEach((l) => {
    (Array.isArray(l[field]) ? l[field] : []).forEach((a, i) => {
      const id = a && a.allocation_id != null ? a.allocation_id : `${l.sales_order_line_id}:${i}`;
      if (!byId.has(id)) byId.set(id, a);
    });
  });
  return [...byId.values()];
}

/** Merge `chain` into the ordered step list, keeping routing order. */
function mergeInto(order, chain) {
  let cursor = -1;
  chain.forEach((c) => {
    const at = order.findIndex((o) => o.id === c.id);
    if (at >= 0) {
      cursor = at;
      return;
    }
    order.splice(cursor + 1, 0, c);
    cursor += 1;
  });
}

const jobLabel = (job) => `Job #${job.master_job_num || job.job_id}`;

/**
 * part_id → { steps, joinsProductId, joinWorkstation, componentOfAssembly }, the
 * order's share summed over every job. `lines` = flattened /truth lines; `soId`
 * = the order being shown (a manifest's pieces count for the order the paper is
 * for; without an id every paper falls back to the job's share).
 */
export function partStepTotals(lines, productionData, soId = null) {
  const linesByPart = new Map();
  (lines || []).forEach((l) => {
    if (!l || l.consumable_id || l.part_id == null) return;
    if (!linesByPart.has(l.part_id)) linesByPart.set(l.part_id, []);
    linesByPart.get(l.part_id).push(l);
  });

  // Every job's chain per part, before shares.
  const perPart = new Map();
  (productionData || []).forEach((job) => {
    if (!job) return;
    const { groups } = groupJobTasks(job);
    groups.forEach((g) => {
      if (!linesByPart.has(g.part_id)) return;
      const chain = chainForGroup(g);
      if (chain.length === 0) return;
      if (!perPart.has(g.part_id)) perPart.set(g.part_id, { jobs: [], joins: {} });
      const entry = perPart.get(g.part_id);
      entry.jobs.push({ job, chain, qty: jobPartQty(chain), allocated: job._source === "allocated" });
      if (g.sharedOnly) {
        entry.joins.joinsProductId = entry.joins.joinsProductId || g.joinsProductId;
        entry.joins.joinWorkstation = entry.joins.joinWorkstation || g.joinWorkstation;
        entry.joins.componentOfAssembly = entry.joins.componentOfAssembly || g.componentOfAssembly;
        entry.joins.sharedOnly = entry.joins.sharedOnly !== false;
      } else {
        entry.joins.sharedOnly = false;
      }
    });
  });

  const out = {};
  perPart.forEach((entry, partId) => {
    const partLines = linesByPart.get(partId);
    const incoming = uniqueAllocations(partLines, "incoming_allocations");
    const outgoing = uniqueAllocations(partLines, "outgoing_allocations");
    const ownQty = entry.jobs.filter((j) => !j.allocated).reduce((a, j) => a + j.qty, 0);
    const outQty = outgoing.reduce((a, x) => a + allocQty(x), 0);
    const ownShare = ownQty > 0 ? Math.max(0, ownQty - outQty) / ownQty : 1;

    const order = [];
    const addRaw = (a, b) => {
      a.back += b.back;
      a.out += b.out;
      a.staged += b.staged;
      a.scrapped += b.scrapped;
    };
    const zero = () => ({ back: 0, out: 0, staged: 0, scrapped: 0 });
    entry.jobs.forEach(({ job, chain, qty, allocated }) => {
      let share = ownShare;
      let note = outQty > 0 && ownQty > 0 ? `${outQty} allocated to other orders` : null;
      if (allocated) {
        const mine = incoming.filter((a) => a && a.source_job_id === job.job_id).reduce((a, x) => a + allocQty(x), 0);
        if (mine <= 0 || qty <= 0) return;
        share = Math.min(1, mine / qty);
        note = `${job._source_so_number || "another order"}'s job: ${Math.min(mine, qty)} of its ${qty} are this order's`;
      }
      const ctx = { soId, share, allocated, homeSoId: allocated ? job._source_so_id ?? null : null };
      const scaled = chain.map((c) => {
        // Shared laser / list steps carry no tasks of their own: pieces cut × share.
        // A task on a manifest counts what came back on this order's papers; one with
        // no paper of this order counts its completed pieces × share.
        let plain = c.taskList.length ? 0 : c.done * share;
        const raw = zero();
        const papers = [];
        let ownTaskQty = 0;
        c.taskList.forEach((t) => {
          if (!allocated) ownTaskQty += num(t.quantity);
          const r = attributeTask(t, ctx);
          if (!r) {
            plain += num(t.qty_completed) * share;
            return;
          }
          addRaw(raw, r);
          papers.push(...r.papers.map((p) => ({ ...p, job: jobLabel(job) })));
        });
        return {
          ...c,
          planned: floorPieces(c.planned * share),
          defects: share === 1 ? c.defects : floorPieces(c.defects * share),
          acc: {
            plain: allocated ? plain : 0,
            ownPlain: allocated ? 0 : plain,
            alloc: allocated ? raw : zero(),
            own: allocated ? zero() : raw,
            ownTaskQty,
            ownTasks: !allocated && c.taskList.length > 0,
          },
          papers,
          outside: c.taskList.some((t) => linesOf(t).length > 0),
          contributions: [{ job: jobLabel(job), done: floorPieces(plain + raw.back), note }],
        };
      });
      scaled.forEach((c) => {
        const o = order.find((x) => x.id === c.id);
        if (!o) return;
        o.planned += c.planned;
        o.defects += c.defects;
        o.running = o.running || c.running;
        o.tasks += c.tasks;
        o.completedTasks += c.completedTasks;
        o.acc.plain += c.acc.plain;
        o.acc.ownPlain += c.acc.ownPlain;
        addRaw(o.acc.alloc, c.acc.alloc);
        addRaw(o.acc.own, c.acc.own);
        o.acc.ownTaskQty += c.acc.ownTaskQty;
        o.acc.ownTasks = o.acc.ownTasks || c.acc.ownTasks;
        o.papers = o.papers.concat(c.papers);
        o.outside = o.outside || c.outside;
        o.contributions = o.contributions.concat(c.contributions);
      });
      mergeInto(order, scaled);
    });
    // Finish each step once. This order's own jobs never count more of a step than
    // their pieces less what the order allocated out (SO-1131: 513 − 93 = 420 of its
    // three FAB311162 coating tasks), back first, then out, then staged.
    order.forEach((st) => {
      const a = st.acc;
      const own = { ...a.own };
      if (soId != null && a.ownTasks) {
        let room = Math.max(0, a.ownTaskQty * ownShare - a.ownPlain);
        own.back = Math.min(own.back, room);
        room -= own.back;
        own.out = Math.min(own.out, room);
        room -= own.out;
        own.staged = Math.min(own.staged, Math.max(0, room));
      }
      const raw = zero();
      addRaw(raw, own);
      addRaw(raw, a.alloc);
      const os = finishOutside(raw, st.papers, null, a.plain + a.ownPlain);
      st.done = os.back;
      st.out = os.out;
      st.staged = os.staged;
      delete st.acc;
    });
    out[partId] = { steps: order, ...entry.joins };
  });
  return out;
}

/** Steps from a part's routing string ("Laser,Coating") for a part with no job yet. */
export function routingSteps(processSteps) {
  const names = String(processSteps || "")
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s && s.toLowerCase() !== "shipping");
  if (names.length === 0) return [];
  const seen = {};
  return names.map((name) => {
    const key = `${name}|`;
    seen[key] = (seen[key] || 0) + 1;
    return { id: `${key}#${seen[key]}`, key, label: name, workstation: name, done: 0, planned: 0, defects: 0, running: false, tasks: 0, completedTasks: 0, out: 0, staged: 0, papers: [], outside: false, contributions: [], fromRouting: true };
  });
}

/** Chip colour for a step on one line. */
export function lineStepState(done, ordered, running) {
  if (done > 0 && done >= ordered) return "done";
  if (done > 0 || running) return "partial";
  return "zero";
}

/**
 * sales_order_line_id → { steps: [{key, label, qty, qtyLabel?, sub?, out, staged, state, title, defects}], fromRouting }
 * `routings` = part_id → process_steps string, for parts no job carries yet.
 * `opts.soId` = the order shown (manifest attribution), `opts.today` = the shop's
 * YYYY-MM-DD (overdue manifests). Purchased (consumable) lines get nothing.
 */
export function lineStepsByLine(lines, productionData, routings = {}, opts = {}) {
  const { soId = null, today = null } = opts || {};
  const flat = (lines || []).filter((l) => l && !l.consumable_id && l.part_id != null && l.sales_order_line_id != null);
  const totals = partStepTotals(flat, productionData, soId);
  const linesByPart = new Map();
  flat.forEach((l) => {
    if (!linesByPart.has(l.part_id)) linesByPart.set(l.part_id, []);
    linesByPart.get(l.part_id).push(l);
  });

  const out = {};
  linesByPart.forEach((partLines, partId) => {
    const total = totals[partId];
    const fromRouting = !total;
    const steps = total ? total.steps : routingSteps(routings[partId]);
    if (steps.length === 0) return;

    // Waterfall each step's count over the lines sharing the part. Pieces out
    // (and staged) follow the pieces done: a line's share of "done or out" less
    // its share of "done".
    const spread = (total) => {
      let rem = total;
      return partLines.map((l, i) => {
        const ordered = num(l.quantity_ordered);
        let take = Math.min(rem, ordered);
        rem -= take;
        if (i === partLines.length - 1) {
          take += rem;
          rem = 0;
        }
        return take;
      });
    };
    const shares = steps.map((s) => spread(s.done));
    const reachedOut = steps.map((s) => spread(s.done + num(s.out)));
    const reachedStaged = steps.map((s) => spread(s.done + num(s.out) + num(s.staged)));

    partLines.forEach((l, li) => {
      const ordered = num(l.quantity_ordered);
      const earlierFull = (si) => partLines.slice(0, li).every((pl, pi) => shares[si][pi] >= num(pl.quantity_ordered));
      const lineSteps = steps.map((s, si) => {
        const qty = shares[si][li];
        const out = Math.max(0, reachedOut[si][li] - qty);
        const staged = Math.max(0, reachedStaged[si][li] - reachedOut[si][li]);
        // A running task shows on the first line it is still filling.
        const running = s.running && qty < ordered && earlierFull(si);
        const step = { key: s.id, label: s.label, qty, out, staged, defects: s.defects };
        const papersOut = (s.papers || []).filter((p) => p.out >= 1 - 1e-9);
        const dueBack = papersOut.map((p) => p.due_back).filter(Boolean).sort()[0] || null;
        const vendors = [...new Set(papersOut.map((p) => p.vendor_name).filter(Boolean))];
        if (qty > 0 && qty >= ordered) step.state = "done";
        else if (out > 0) {
          const overdue = !!(dueBack && today && dueBack < today);
          step.state = overdue ? "out_late" : "out";
          step.qtyLabel = qty > 0 ? `${qty} back · ${out} out` : `${out} out`;
          step.sub = `${vendorLabel(vendors)}${dueBack ? ` · ${overdue ? "was due" : "due"} ${monthDay(dueBack)}` : ""}`;
        } else if (staged > 0) {
          step.state = "staged";
          step.qtyLabel = qty > 0 ? `${qty} back · ${staged} staged` : `${staged} staged`;
          step.sub = "draft manifest";
        } else step.state = lineStepState(qty, ordered, running);
        step.title = stepTitle(s, qty, ordered, step.state, partLines.length > 1, { out, staged, today });
        return step;
      });
      if (total && total.sharedOnly && (total.joinsProductId || total.componentOfAssembly)) {
        lineSteps.push({
          key: "join",
          label: total.joinsProductId ? `Joins ${total.joinsProductId}` : "Consumed in assembly",
          sub: total.joinWorkstation ? `at ${total.joinWorkstation}` : total.componentOfAssembly || null,
          state: "join",
        });
      }
      out[l.sales_order_line_id] = { steps: lineSteps, fromRouting };
    });
  });
  return out;
}

function stepTitle(step, qty, ordered, state, shared, extra = {}) {
  if (step.fromRouting) return `${step.label}: no job yet (from the part's routing) · 0 of ${ordered}`;
  const parts = [`${step.label}: ${qty} of ${ordered} ${step.outside ? "back" : "done"}`];
  if (extra.out > 0) parts.push(`${extra.out} at the vendor now`);
  if (extra.staged > 0) parts.push(`${extra.staged} on a draft manifest, not sent yet`);
  if (state === "done" && qty > ordered) parts[0] += ` (+${qty - ordered} extra)`;
  if (step.tasks > 0) {
    const allTasksDone = step.completedTasks === step.tasks;
    if (allTasksDone && qty < ordered && state !== "zero") parts.push(`every task here is complete: ${ordered - qty} more need a job`);
    else parts.push(`${step.completedTasks} of ${step.tasks} task${step.tasks === 1 ? "" : "s"} complete${step.running ? ", running now" : ""}`);
  }
  if (step.defects > 0) parts.push(`${step.defects} defective`);
  if (shared) parts.push("this part is on more than one line: the count is split across them");
  (step.contributions || []).forEach((c) => parts.push(`${c.job}: ${c.done}${c.note ? ` (${c.note})` : ""}`));
  if (step.papers && step.papers.length) parts.push(...paperLines(step.papers, extra.today));
  return parts.join("\n");
}
